import { useEffect, useState } from "react";
import { listFolderFileNames } from "../../../common/utils/s3Utils";
import { getFileNameFromS3Key } from "./Utils";

// One listing per folder, shared by every cell showing it and kept across page changes and sorts.
// The table's Refresh clears it (see clearFolderListings) so files written since are picked up.
// A failed listing is dropped so the next render retries it rather than keeping the fallback label.
const listings = new Map();

export const clearFolderListings = () => listings.clear();

const listOnce = (s3Key, bucket, domain) => {
    const cacheKey = `${bucket}/${s3Key}`;
    if (!listings.has(cacheKey)) {
        listings.set(cacheKey, listFolderFileNames(s3Key, bucket, domain)
            .catch(error => {
                console.warn(`Folder listing failed for ${cacheKey}; the cell keeps its fallback label`, error);
                listings.delete(cacheKey);
                return null;
            }));
    }
    return listings.get(cacheKey);
};

/** Display name for a folder's contents: the file name when there is one file, "N files" otherwise. */
export const folderDisplayName = fileNames => {
    if (!Array.isArray(fileNames) || fileNames.length === 0) {
        return null;
    }
    return fileNames.length === 1 ? fileNames[0] : `${fileNames.length} files`;
};

/** What a stored key holds: `fileName` to show (null while unknown) and `isEmpty` once a folder key was listed and held nothing. */
export default function useFolderFiles(s3Key, bucket, domain) {
    const directName = getFileNameFromS3Key(s3Key);
    const [listed, setListed] = useState(undefined);
    const listable = !directName && Boolean(s3Key && bucket);

    useEffect(() => {
        setListed(undefined);
        if (!listable) {
            return undefined;
        }
        let active = true;
        listOnce(s3Key, bucket, domain).then(names => {
            if (active) {
                setListed(names);
            }
        });
        return () => { active = false; };
    }, [listable, s3Key, bucket, domain]);

    if (directName) {
        return { fileName: directName, isEmpty: false };
    }
    return {
        fileName: listable ? folderDisplayName(listed) : null,
        isEmpty: listable && Array.isArray(listed) && listed.length === 0
    };
}
